import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, MessageSquareQuote } from 'lucide-react';
import { ProductReview } from '../../types';
import { reviewService } from '../../services/ReviewService';
import { ReviewSummary } from './ReviewSummary';
import { CustomerMediaCarousel } from './CustomerMediaCarousel';
import { ReviewFeed } from './ReviewFeed';
import { ReviewLightboxModal } from './ReviewLightboxModal';

interface AllReviewsViewProps {
    onClose: () => void;
}

export const AllReviewsView: React.FC<AllReviewsViewProps> = ({ onClose }) => {
    const [reviews, setReviews] = useState<ProductReview[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        const loadReviews = async () => {
            try {
                const data = await reviewService.getAllApprovedReviews();
                setReviews(data);
            } catch (error) {
                console.error("Error loading reviews", error);
            } finally {
                setIsLoading(false);
            }
        };
        loadReviews();
    }, []);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="min-h-screen bg-black pb-24"
        >
            {/* Header Sticky */}
            <div className="sticky top-0 z-40 bg-black/80 backdrop-blur-md border-b border-zinc-900">
                <div className="container mx-auto px-4 h-16 flex items-center justify-between">
                    <button
                        onClick={onClose}
                        className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-400 hover:text-white transition-colors"
                    >
                        <ArrowLeft size={16} />
                        Volver
                    </button>
                    
                    <span className="text-xs font-mono uppercase tracking-widest text-zinc-600 hidden sm:block">
                        Social Proof
                    </span>
                </div>
            </div>

            <div className="container mx-auto px-4 pt-10 md:pt-16 max-w-4xl">
                {/* Hero Title */}
                <div className="mb-12 md:mb-20 text-center">
                    <div className="inline-flex items-center justify-center gap-3 mb-4">
                        <MessageSquareQuote className="text-zinc-500" size={24} />
                    </div>
                    <h1 
                        className="text-4xl md:text-6xl font-display font-black uppercase italic text-white mb-4 leading-tight"
                        style={{ filter: 'drop-shadow(0 0 15px rgba(255,255,255,0.6)) drop-shadow(0 0 40px rgba(255,255,255,0.3))' }}
                    >
                        Voces de<br/>Confianza
                    </h1>
                    <p className="text-zinc-500 font-mono text-xs md:text-sm uppercase tracking-widest max-w-lg mx-auto">
                        Comentarios reales y fotografías de clientes verificados que ya forman parte del equipo.
                    </p>
                </div>

                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-32 space-y-4">
                        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">Cargando reseñas...</span>
                    </div>
                ) : reviews.length === 0 ? (
                    <div className="py-32 text-center">
                        <p className="text-zinc-500 font-mono text-sm uppercase tracking-widest">Aún no hay reseñas disponibles.</p>
                    </div>
                ) : (
                    <div className="space-y-12 md:space-y-20">
                        {/* Summary & Analytics */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center bg-zinc-900/20 border border-zinc-900 p-6 md:p-10 rounded-sm">
                            <div className="hidden md:block">
                                <h3 className="text-xl font-display font-bold uppercase italic text-white mb-2">Calidad Comprobada</h3>
                                <p className="text-zinc-500 text-sm">Nuestro compromiso con la excelencia reflejado en los comentarios de cada uno de nuestros clientes.</p>
                            </div>
                            <ReviewSummary reviews={reviews} />
                        </div>

                        {/* Media Carousel */}
                        <div>
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-lg font-display font-bold uppercase italic text-white">Galería de Clientes</h3>
                                <span className="text-[10px] font-mono text-zinc-600 uppercase">Fotos Verificadas</span>
                            </div>
                            <CustomerMediaCarousel reviews={reviews} />
                        </div>

                        {/* Complete Feed */}
                        <div>
                            <div className="flex items-center justify-between mb-8 border-b border-zinc-900 pb-4">
                                <h3 className="text-lg font-display font-bold uppercase italic text-white">Últimos Comentarios</h3>
                                <span className="text-xs font-bold text-white bg-zinc-900 px-3 py-1 rounded-sm">{reviews.length}</span>
                            </div>
                            <ReviewFeed 
                                reviews={reviews} 
                                onReviewClick={(reviewId) => {
                                    const index = reviews.findIndex(r => r.id === reviewId);
                                    if (index !== -1) setLightboxIndex(index);
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Lightbox Modal */}
            <ReviewLightboxModal 
                isOpen={lightboxIndex !== null}
                reviews={reviews}
                initialIndex={lightboxIndex || 0}
                onClose={() => setLightboxIndex(null)}
            />
        </motion.div>
    );
};
